import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  Phone, 
  Mail, 
  MessageSquare, 
  Users, 
  FileText, 
  DollarSign, 
  Package,
  Clock,
  TrendingUp,
  UserPlus
} from 'lucide-react';
import { useTimezoneUtils } from '@/hooks/useTimezoneUtils';

interface ActivityItem {
  id: string;
  type: 'lead_created' | 'lead_activity' | 'communication' | 'payment' | 'job_created' | 'job_status' | 'part_added';
  timestamp: string;
  description: string;
  metadata?: any;
  user?: { full_name: string };
}

interface CustomerActivityTimelineProps {
  activities: ActivityItem[];
}

const activityIcons = {
  lead_created: UserPlus,
  lead_activity: FileText,
  communication: MessageSquare,
  payment: DollarSign,
  job_created: TrendingUp,
  job_status: Clock,
  part_added: Package,
};

const activityColors = {
  lead_created: 'text-success bg-success/10',
  lead_activity: 'text-info bg-info/10',
  communication: 'text-primary bg-primary/10',
  payment: 'text-success bg-success/10',
  job_created: 'text-warning bg-warning/10',
  job_status: 'text-warning bg-warning/10',
  part_added: 'text-muted-foreground bg-muted',
};

const getCommIconByType = (commType: string) => {
  switch (commType) {
    case 'call': return Phone;
    case 'email': return Mail;
    case 'sms': return MessageSquare;
    case 'meeting': return Users;
    default: return MessageSquare;
  }
};

export function CustomerActivityTimeline({ activities }: CustomerActivityTimelineProps) {
  const { formatDate } = useTimezoneUtils();
  
  if (!activities || activities.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Activity Timeline
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground text-center py-8">No activity recorded yet</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Clock className="h-5 w-5" />
          Activity Timeline
          <Badge variant="secondary" className="ml-auto">
            {activities.length} events
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <ScrollArea className="h-[600px] pr-4">
          <div className="space-y-4">
            {activities.map((activity, index) => {
              const Icon = activity.type === 'communication' && activity.metadata?.communication_type
                ? getCommIconByType(activity.metadata.communication_type)
                : activityIcons[activity.type] || FileText;
              
              const colorClass = activityColors[activity.type] || 'text-muted-foreground bg-muted';

              return (
                <div key={activity.id} className="flex gap-4 relative">
                  {/* Timeline line */}
                  {index < activities.length - 1 && (
                    <div className="absolute left-[19px] top-10 bottom-0 w-[2px] bg-border" />
                  )}
                  
                  {/* Icon */}
                  <div className={`flex-shrink-0 h-10 w-10 rounded-full flex items-center justify-center ${colorClass} z-10`}>
                    <Icon className="h-5 w-5" />
                  </div>
                  
                  {/* Content */}
                  <div className="flex-1 pb-6">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1">
                        <p className="font-medium text-sm">{activity.description}</p>
                        {activity.user && (
                          <p className="text-xs text-muted-foreground mt-1">
                            by {activity.user.full_name}
                          </p>
                        )}
                        
                        {/* Additional metadata display */}
                        {activity.metadata?.amount && (
                          <Badge variant="outline" className="mt-2 text-success">
                            +${parseFloat(activity.metadata.amount).toFixed(2)}
                          </Badge>
                        )}
                        {activity.metadata?.status && (
                          <Badge variant="outline" className="mt-2">
                            {activity.metadata.status}
                          </Badge>
                        )}
                      </div>
                      
                      <span className="text-xs text-muted-foreground whitespace-nowrap">
                        {formatDate(new Date(activity.timestamp), 'MMM dd, h:mm a')}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </ScrollArea>
      </CardContent>
    </Card>
  );
}